// src/index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './index.css';
import App from './App';
import theme from './theme';
import {UserProvider} from "./components/context/UserContext";
import { Auth0Provider } from '@auth0/auth0-react';
import { auth0Config } from './auth0-config';
import { Auth0UserProvider } from './components/context/Auth0UserContext';
import ErrorBoundary from './components/ErrorBoundary';
import { attemptChunkLoadRecovery } from './utils/chunkLoadRecovery';
import './lib/supabaseClient'; // Initialize Supabase connection test on startup

// Handle chunk load errors (stale cache after deployment) - auto reload once
window.addEventListener('error', (event) => {
  attemptChunkLoadRecovery(event.error ?? event.message);
});

// Dynamic import failures may surface as promise rejections instead of errors.
window.addEventListener('unhandledrejection', (event) => {
  attemptChunkLoadRecovery(event.reason);
});

// Initialize Sentry for error tracking (must be before other code)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || 'development',
    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    // Session replay for debugging
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    // Don't send PII
    beforeSend(event: Sentry.Event) {
      // Scrub sensitive data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['X-User-ID'];
      }
      return event;
    },
  });
}

// Simple switch between Auth0 and original authentication
const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Conditionally render Auth0Provider or original UserProvider based on environment variable
if (useAuth0) {
  const onRedirectCallback = (appState: any) => {
    // Store the returnTo URL in sessionStorage so Auth0Callback can access it
    if (appState?.returnTo) {
      sessionStorage.setItem('auth0_returnTo', appState.returnTo);
    }
  };

  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            authorizationParams={{
              redirect_uri: auth0Config.redirectUri,
              audience: auth0Config.audience,
              scope: auth0Config.scope,
            }}
            onRedirectCallback={onRedirectCallback}
            cacheLocation="localstorage"
            useRefreshTokens={true}
          >
            <Auth0UserProvider>
              <App />
            </Auth0UserProvider>
          </Auth0Provider>
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <UserProvider>
            <App />
          </UserProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>
  );
}
